import { logger } from 'client/utils/logger/logger';

/**
 * Parse JSON cookie string.
 *
 * @param {String} str
 * @return {Object} Parsed object or undefined if not json cookie
 * @public
 */

function JSONCookie(str) {
  if (typeof str !== 'string' || str.substr(0, 2) !== 'j:') {
    return undefined;
  }

  try {
    return JSON.parse(str.slice(2));
  } catch {
    return undefined;
  }
}

/**
 * Gets a cookie value by name from the document. If no name is provided, it returns all cookies.
 * This function is intended for client-side use only
 *
 * @example
 * const location = getCookie('location')
 * const { location, EdmundsYear } = getCookie(); // more performant than getting the two cookies one by one
 *
 * @param {string} [name] - The name of the cookie to retrieve. If omitted, all cookies are returned.
 * @returns {string|object|undefined} The cookie value, or an object of all cookies if name is omitted, or undefined if the cookie doesn't exist.
 */
export const getCookie = name => {
  const cookies = document.cookie.split(';').reduce((acc, cookie) => {
    const [key, rawValue] = cookie.trim().split(/=(.*)/s, 2);

    let decodedValue;
    if (key) {
      try {
        decodedValue = decodeURIComponent(rawValue);
      } catch (e) {
        logger.error(`Error on GetCookie: ${e.message} happened on cookie ${key} with value ${rawValue}`);
      }
      const jsonCookie = JSONCookie(decodedValue);
      if (jsonCookie) {
        acc[key] = jsonCookie;
      } else {
        acc[key] = decodedValue;
      }
    }

    return acc;
  }, {});

  return name === undefined ? cookies : cookies[name];
};
